import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearTiersData, fetchTiers, setSelectedTiers } from '../reduxStore/TiersSlice';
import { AppDispatch } from '../reduxStore/Store';
import MyTable from '../components/MyTable';
import { ButtonType, TypeTiers, GeneriqueDataType } from '../constants/enums';
import { TableHeader, TableAction } from '../constants/interfaces';
import { Tiers } from '../models/models';
import TiersForm from '../components/Forms/TiersForm';
import { Box, Collapse, DialogTitle, Typography } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ImportTiersDialog from '../components/Forms/ImportTiersDialog';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { labels } from '../constants/labels';
import { getLabels } from '../constants/fieldLabels';
import { useDialog } from '../components/DialogComponent';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppOutlineButton from '../components/layout/AppOutlineButton';
import FamilleHierarchie from './FamilleHierarchie';
import TiersFilters, { TiersFilterValues } from '../components/Filters/TiersFilters';

/** Type de catégorie generique_data associé à une nature de tiers (null si non catégorisable). */
const categorieTypeForTiers = (t: TypeTiers): GeneriqueDataType | null =>
  t === TypeTiers.CLIENT      ? GeneriqueDataType.CATEGORIE_CLIENT
  : t === TypeTiers.FOURNISSEUR ? GeneriqueDataType.CATEGORIE_FOURNISSEUR
  : null;

interface TiersFormProps {
  tiersType: TypeTiers;
}

const EMPTY_FILTERS: TiersFilterValues = { nom: null, email: null, telephone: null, categorie_id: null, partage: [] };

const GestionTiers: React.FC<TiersFormProps> = ({ tiersType }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { tiersData, total, loading, error, successMessage } = useSelector((state: any) => state.TiersSlice);
  const selectedTiers = useSelector((state: any) => state.TiersSlice.selectedTiers);

  const [filters, setFilters] = useState<TiersFilterValues>(EMPTY_FILTERS);
  const [showHierarchie, setShowHierarchie] = useState(false);
  const categorieType = categorieTypeForTiers(tiersType);
  const [fetchParams, setFetchParams] = useState({
    type: tiersType as string,
    limit: 5,
    offset: 0,
  });

  const [FormDialog, openDialog, closeDialog] = useDialog({
    dialogProps: { maxWidth: 'md', fullWidth: true }
  });

  const [importOpen, setImportOpen] = useState(false);

  // Pas de clear ici : il s'exécuterait à chaque page/filtre → la table saute.
  useEffect(() => {
    const request = dispatch(fetchTiers({ ...fetchParams, ...filters }));
    return () => request.abort();
  }, [dispatch, tiersType, fetchParams, filters, successMessage]);

  // Nettoyage uniquement au démontage
  useEffect(() => {
    return () => {
      dispatch(setSelectedTiers(null));
      dispatch(clearTiersData());
    };
  }, [dispatch]);

  // Reset pagination + filtres au changement de type (client ↔ fournisseur)
  useEffect(() => {
    setFetchParams({ type: tiersType, limit: 5, offset: 0 });
    setFilters(EMPTY_FILTERS);
  }, [tiersType]);

  // Reset offset à 0 quand les filtres changent — sinon on peut tomber sur une
  // page invalide (ex: page 5 d'un résultat qui tient sur 1 page après filtre).
  const handleFiltersChange = (next: TiersFilterValues) => {
    setFilters(next);
    setFetchParams(prev => ({ ...prev, offset: 0 }));
  };

  const handleFiltersReset = () => {
    setFilters(EMPTY_FILTERS);
    setFetchParams(prev => ({ ...prev, offset: 0 }));
  };

  const handleCancel = () => {
    closeDialog();
    dispatch(setSelectedTiers(null));
  };

  const headers: TableHeader<Tiers>[] = [
    { key: "nom", label: "Nom" },
    { key: "prenom", label: "Prenom" },
    { key: "telephone", label: "Telephone" },
    { key: "email", label: "Email" },
    { key: "categorie_nom", label: "Catégorie" },
    {
      key: "departements_partage_noms", label: "Départements", hideBelow: "md",
      render: (row) => row.departements_partage_noms || "Tous",
    },
  ];

  const tableActions: TableAction<Tiers>[] = [
    {
      label: "Edit",
      callback: (tiers) => {
        dispatch(setSelectedTiers(tiers));
        openDialog();
      },
      type: ButtonType.EDIT,
    },
  ];

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setFetchParams(prev => ({
      ...prev,
      offset: newPage * newRowsPerPage,
      limit: newRowsPerPage,
    }));
  };

  const label = tiersType === TypeTiers.FOURNISSEUR ? labels.fournisseur : labels.client;
  const labelPluriel = tiersType === TypeTiers.FOURNISSEUR ? labels.fournisseurPluriel : labels.clientPluriel;

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={`Gestion ${labelPluriel}`}
        subtitle={`${total} ${labelPluriel.toLowerCase()} enregistré${total > 1 ? 's' : ''}`}
        icon={<PeopleIcon />}
        addLabel={`Ajouter un ${label}`}
        onAdd={openDialog}
        actions={
          <Box display="flex" gap={1}>
            <AppOutlineButton
              startIcon={<FileUploadIcon />}
              onClick={() => setImportOpen(true)}
            >
              Importer Excel
            </AppOutlineButton>
            {categorieType && (
              <AppOutlineButton
                startIcon={<AccountTreeIcon />}
                onClick={() => setShowHierarchie(prev => !prev)}
              >
                {showHierarchie ? 'Masquer catégories' : 'Catégories'}
              </AppOutlineButton>
            )}
          </Box>
        }
      />

      <ImportTiersDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        tiersType={tiersType}
        labelPluriel={labelPluriel}
        onImported={() => {
          setImportOpen(false);
          dispatch(fetchTiers({ ...fetchParams, ...filters }));
        }}
      />

      {error &&<Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      {categorieType && (
        <Collapse in={showHierarchie}>
          <AppSurface sx={{ mb: 2 }}>
            <FamilleHierarchie type={categorieType} entityLabel="catégorie" />
          </AppSurface>
        </Collapse>
      )}

      <AppSurface sx={{ mb: 2 }} padding={0}>
        <TiersFilters
          values={filters}
          onChange={handleFiltersChange}
          onReset={handleFiltersReset}
          categorieType={categorieType}
        />
      </AppSurface>

      <AppSurface>
        <MyTable
          headers={headers}
          data={tiersData}
          onPageChange={handlePageChange}
          actions={tableActions}
          totalRows={total}
          loading={loading}
          showDetails
          fieldLabels={getLabels('Tiers')}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          <Typography variant="h6">
            {selectedTiers ? `Modifier ${label}` : `Ajouter un ${label}`}
          </Typography>
        </DialogTitle>
        <TiersForm onCancel={handleCancel} tiersType={tiersType} />
      </FormDialog>
    </AppPage>
  );
};

export default GestionTiers;
